import { markPendingSnap } from "../../../utils/pendingSnap";
import { PlanTrailActionContext, getMap, getLayerRegistry, safeRemoveLayer } from "./types";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { snapPoints } from "../../../../../utils/models/Graphooper";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { hiikerElevation } from "../../../../../utils/helpers/HiikerElevationApiHelper";
import { haversineMeters } from "../../../services/stage-connectors";
import { attachedDestinationAtCoord } from "../../../services/stage-destinations";
import { bridgeCoordinate, fillBridgeElevations } from "../../../services/off-network-bridge";
import { knownMarkerElevation } from "../../../../../utils/elevation/repairElevations";
import { edgeValues, toRanges } from "../../../../../utils/models/segmentSurfaces";
import { plainToClass } from "class-transformer";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import mapboxgl from "mapbox-gl";
import { HoverOptimizationConfig } from "../../../services/HoverOptimizationConfig";
import { ROUTE_STYLES } from "../../../constants/route-styles";
import { getRouteStyleSettings, buildRouteStyleExpressions } from "../../../../../utils/routeStyleSettings";

// One-time flag: hover-cursor listeners for non-active route lines
let inactiveLineHoverAttached = false;

// When a stage endpoint is pinned to a destination, a snapped route endpoint
// further than this from the item is treated as "no known route" — the gap is
// drawn as a straight dashed tail instead of snapping the marker onto the net.
const OFF_NETWORK_TOLERANCE_M = 25;

/**
 * Terrain elevations along the straight bridge from a routed endpoint out to an
 * attached item. Returned coordinates run FROM `anchor` TO `item`, with
 * `anchor` itself first.
 *
 * The bridge is ground the user actually walks, so its profile is sampled from
 * the elevation service rather than interpolated — flattening it would erase a
 * genuine descent to a valley hotel. `fillBridgeElevations` only patches points
 * the service couldn't supply (it degrades to a plain straight line when
 * unreachable), so a working service is always what shapes the profile.
 */
async function sampledBridge(anchor: number[], item: number[]): Promise<number[][]> {
  const stamp = Date.now().toString();
  const anchorMarker = new MarkerFeature(
    new MarkerGeometry([anchor[0], anchor[1]]),
    new MarkerProperties(generateID(), true, stamp, anchor[2] ?? 0)
  );
  const itemMarker = new MarkerFeature(
    new MarkerGeometry([item[0], item[1]]),
    new MarkerProperties(generateID(), true, stamp, item[2] ?? 0)
  );

  let sampled: number[][];
  try {
    const line = await hiikerElevation(anchorMarker, itemMarker);
    sampled = line.lineStringGeometry.coordinates;
  } catch (err) {
    // hiikerElevation already swallows service errors internally; this only
    // catches something unexpected. Either way the bridge still has to exist,
    // so fall back to its two endpoints.
    console.error('Elevation sampling failed for an off-network bridge:', err);
    sampled = [];
  }

  // A sample set that doesn't actually span the gap is no use — keep the real
  // endpoints so the geometry is never wrong, only the profile approximate.
  if (sampled.length < 2) sampled = [anchor, item];

  const filled = fillBridgeElevations(sampled, anchor[2]);
  // Pin the ends: the anchor keeps the routed line's own elevation, and the
  // item must stay exactly where it was attached.
  filled[0] = [anchor[0], anchor[1], filled[0][2]];
  filled[filled.length - 1] = [item[0], item[1], filled[filled.length - 1][2]];
  return filled;
}

export const polylineActions = {
  // Legacy function - removed. Use addAllRoutesToMap instead.

  /**
   * REFACTOR:
   * 1. Immediately return a provisional line (straight line)
   * 2. Kick off the real fetch in the background; replace it once done
   */
  async createPolylineGeoJson(
    { state, dispatch }: PlanTrailActionContext,
    { startMarker, endMarker }: { startMarker: MarkerFeature; endMarker: MarkerFeature }
  ): Promise<LineStringFeature> {
    // Check if markers are valid
    if (!startMarker || !endMarker || !startMarker.markerGeometry || !endMarker.markerGeometry) {
      console.error('Invalid markers passed to createPolylineGeoJson', { startMarker, endMarker });
      // Return empty linestring
      return LineStringFeature.coordsOnlyFeature([]);
    }

    // 1) provisional straight line — drawn faint and dotted until the real one lands
    const provisional = markPendingSnap(LineStringFeature.straightLine(startMarker, endMarker));

    // 2) background real-line fetch
    if (endMarker.markerProperties.isSnap) {
      // snapped network route
      snapPoints(startMarker, endMarker, state.routePreference)
        .then(async (snappedLine: LineStringFeature) => {
          let coords = snappedLine.lineStringGeometry.coordinates;

          // if previous marker was non-snap, prepend its exact click point
          if (!startMarker.markerProperties.isSnap) {
            // Never a placeholder 0: if the marker's lookup failed, borrow the elevation of
            // the snapped point it joins, which is metres away.
            const ele = knownMarkerElevation(startMarker) ?? coords[0]?.[2];
            const lon = startMarker.markerGeometry.lon();
            const lat = startMarker.markerGeometry.lat();
            // A click point already on the line would be dropped as a repeat by the next cleanup,
            // taking the surfaces' shift with it.
            if (coords[0]?.[0] !== lon || coords[0]?.[1] !== lat) {
              const surface = snappedLine.lineStringProperties.extras.surface;
              // The connector is a new first edge, and nothing says what it crosses.
              surface.values = toRanges([undefined, ...edgeValues(surface.values, coords.length)]);
              coords = [Number.isFinite(ele) ? [lon, lat, ele] : [lon, lat], ...coords];
            }
          }

          // A segment endpoint pinned to a stage destination (POI/waypoint/
          // highlight) must stay ON the item, not snap to the nearest routing
          // network node. GraphHopper still routes as far along the network as
          // it can (respecting the route preference); when the item sits off
          // that network its snapped endpoint lands short of it, so we bridge
          // the remaining gap with a straight line. The ROUTED part stays a
          // real route — only the straight bridge is flagged (offNetworkHead/
          // Tail) so just that portion renders dashed.
          const startDest = attachedDestinationAtCoord(
            state.routes, state.stageDestinations, startMarker.markerGeometry.coordinates,
            undefined, state.trailStartDestination
          );
          const endDest = attachedDestinationAtCoord(
            state.routes, state.stageDestinations, endMarker.markerGeometry.coordinates,
            undefined, state.trailStartDestination
          );
          const lsProps = snappedLine.lineStringProperties as any;
          lsProps.offNetworkHead = false;
          lsProps.offNetworkTail = false;
          lsProps.offNetworkFull = false;

          // A bridge adds edges at an end that nothing says the surface of, so
          // the surfaces shift along with them (as the unsnapped start above).
          const surface = lsProps.extras?.surface;
          let surfaceEdges = surface?.values ? edgeValues(surface.values, coords.length) : null;

          // The bridge is real ground, so it gets real terrain elevations
          // sampled along it rather than a synthetic point — an item carries no
          // altitude of its own, and a bare 0 there reads as a sea-level plunge
          // worth hundreds of metres of phantom descent. See
          // services/off-network-bridge.ts.
          if (startDest && coords.length) {
            const poi = bridgeCoordinate(
              startMarker.markerGeometry.lon(),
              startMarker.markerGeometry.lat(),
              startMarker.markerProperties.ele,
              coords[0]
            );
            if (haversineMeters(coords[0], poi) > OFF_NETWORK_TOLERANCE_M) {
              // Routed toward the item but stopped short — bridge the gap,
              // sampled outward from the routed end then reversed to lead in.
              const bridge = await sampledBridge(coords[0], poi);
              const lead = bridge.slice(1).reverse();
              coords = [...lead, ...coords];
              if (surfaceEdges) surfaceEdges = [...lead.map(() => undefined), ...surfaceEdges];
              lsProps.offNetworkHead = true;
            } else {
              // Reachable: end the route on the item, no bridge
              coords[0] = poi;
            }
          }

          if (endDest && coords.length) {
            const last = coords[coords.length - 1];
            const poi = bridgeCoordinate(
              endMarker.markerGeometry.lon(),
              endMarker.markerGeometry.lat(),
              endMarker.markerProperties.ele,
              last
            );
            if (haversineMeters(last, poi) > OFF_NETWORK_TOLERANCE_M) {
              const bridge = await sampledBridge(last, poi);
              const tail = bridge.slice(1);
              coords = [...coords, ...tail];
              if (surfaceEdges) surfaceEdges = [...surfaceEdges, ...tail.map(() => undefined)];
              lsProps.offNetworkTail = true;
            } else {
              coords[coords.length - 1] = poi;
            }
          }

          if (surface && surfaceEdges) surface.values = toRanges(surfaceEdges);

          snappedLine.lineStringGeometry.coordinates = coords;

          // Move the new marker to the snapped endpoint — but NOT when it is
          // pinned to a destination (that would drag it back off the item).
          if (!endDest) {
            await dispatch("updateSnappedMarker", {
              marker: endMarker,
              newCoords: coords[coords.length - 1]
            });
          }

          // swap line
          dispatch("replaceProvisionalLine", {
            provisionalLine: provisional,
            realLine: snappedLine
          });
        })
        .catch(async (err) => {
          // GraphHopper genuinely has no route between these points (e.g. a
          // POI that isn't on any routable way) — fall back to the same
          // elevation-aware straight line the non-snap branch below uses,
          // flagged fully off-network so it renders dashed. Without this the
          // provisional straight line set at the top of this function would
          // silently linger forever, un-flagged and un-snapped, which is
          // what made some POIs look impossible to snap to.
          console.error("snapPoints error, falling back to a straight line:", err);
          try {
            const elevLine = await hiikerElevation(startMarker, endMarker);
            const props = elevLine.lineStringProperties as any;
            props.offNetworkHead = false;
            props.offNetworkTail = false;
            props.offNetworkFull = true;
            dispatch("replaceProvisionalLine", {
              provisionalLine: provisional,
              realLine: elevLine
            });
          } catch (fallbackErr) {
            console.error("hiikerElevation fallback also failed:", fallbackErr);
            // Nothing better is coming: the straight line is what there is.
            dispatch("settleProvisionalLine", provisional.lineStringProperties.id);
          }
        });
    } else {
      // straight route with real elevations
      hiikerElevation(startMarker, endMarker)
        .then((elevLine: LineStringFeature) => {
          // Straight-line route option: the whole leg is a straight line
          // already. If it connects to a destination, dash the whole leg to
          // signal it isn't a known route.
          const touchesDest = !!(
            attachedDestinationAtCoord(
              state.routes, state.stageDestinations, startMarker.markerGeometry.coordinates,
              undefined, state.trailStartDestination
            ) ||
            attachedDestinationAtCoord(
              state.routes, state.stageDestinations, endMarker.markerGeometry.coordinates,
              undefined, state.trailStartDestination
            )
          );
          const props = elevLine.lineStringProperties as any;
          props.offNetworkHead = false;
          props.offNetworkTail = false;
          props.offNetworkFull = touchesDest;
          dispatch("replaceProvisionalLine", {
            provisionalLine: provisional,
            realLine: elevLine
          });
        })
        .catch(err => {
          console.error("hiikerElevation error:", err);
          dispatch("settleProvisionalLine", provisional.lineStringProperties.id);
        });
    }

    return provisional;
  },

  /**
   * A provisional line that will never be replaced (its route and the
   * fallback both failed) stops reading as "still routing".
   */
  async settleProvisionalLine({ commit, dispatch }: PlanTrailActionContext, lineStringId: string | null) {
    if (!lineStringId) return;
    commit("settleLineString", lineStringId);
    await dispatch("updateRouteSources");
  },

  /**
   * REFACTOR:
   * Replaces the provisional line with the real line once data arrives
   */
  async replaceProvisionalLine(
    { state, commit, dispatch, getters }: PlanTrailActionContext,
    {
      provisionalLine,
      realLine
    }: { provisionalLine: LineStringFeature; realLine: LineStringFeature }
  ) {
    console.log('[POLYLINES] replaceProvisionalLine called', {
      provisionalId: provisionalLine.lineStringProperties.id,
      pendingBefore: state.pendingProvisionalReplacements.size,
      pendingIds: Array.from(state.pendingProvisionalReplacements)
    });

    // Locate the route that owns the provisional line — it may not be the
    // active route (whole-trail editing edits routes without activating them)
    let owningKey: string | null = null;
    let idx = -1;
    const searchKeys = state.activeRouteKey && state.routes[state.activeRouteKey]
      ? [state.activeRouteKey, ...Object.keys(state.routes).filter(k => k !== state.activeRouteKey)]
      : Object.keys(state.routes);
    for (const key of searchKeys) {
      const found = state.routes[key].lineStrings.findIndex(
        ls => ls.lineStringProperties.id === provisionalLine.lineStringProperties.id
      );
      if (found >= 0) {
        owningKey = key;
        idx = found;
        break;
      }
    }
    if (owningKey == null || idx < 0) {
      return;
    }

    // remove old and insert new
    commit("removeLineStringAtIndex", { index: idx, routeKey: owningKey });
    commit("addLineStringAtIndex", { lineString: realLine, index: idx, routeKey: owningKey });

    // Update the command-pattern undo manager so the stored linestring
    // reflects the real snapped route instead of the provisional straight line
    const provisionalId = provisionalLine.lineStringProperties.id;
    state.undoManager.updateProvisionalLine(provisionalId, realLine);

    // Legacy tracking for old snapshot-based undo (still needed during migration)
    const wasTracking = state.pendingProvisionalReplacements.has(provisionalId);
    state.pendingProvisionalReplacements.delete(provisionalId);

    console.log('[POLYLINES] After delete, pending size:', state.pendingProvisionalReplacements.size, 'wasTracking:', wasTracking);

    if (wasTracking && state.pendingProvisionalReplacements.size === 0) {
      console.log('[POLYLINES] All provisional lines replaced after marker drag, saving undo state');
      await commit("updateUndo");
    }

    // Only update route polylines — markers and hover are unchanged
    await dispatch("updateRouteSources");
  },

  async updateSnappedMarker(
    { commit, dispatch }: PlanTrailActionContext,
    { marker, newCoords }: { marker: MarkerFeature; newCoords: [number, number, number?] }
  ) {
    marker.markerGeometry.coordinates = newCoords;
    marker.markerProperties.isSnap = true;
    await commit("updateMarker", marker);
    // Only update markers — route lines will be updated by updateLineStringsFromMarkerMove
    await dispatch("updateMarkerSource");
  },

  async updateLineStringsFromMarkerMove(
    { state, commit, dispatch, getters }: PlanTrailActionContext,
    payload: number | { markerIndex: number; routeKey?: string }
  ) {
    // Accepts a bare index (active route, legacy) or { markerIndex, routeKey }
    // so edits work on non-active routes (whole-trail editing).
    const markerIndex = typeof payload === 'number' ? payload : payload.markerIndex;
    const routeKey = (typeof payload === 'object' && payload.routeKey) ? payload.routeKey : state.activeRouteKey;

    const targetRoute = routeKey ? state.routes[routeKey] : getters.activeRoute;
    if (!targetRoute) return;
    const activeMarkers = targetRoute.markers;
    let indexes: number[] = [];
    if (markerIndex == 0) {
      indexes.push(0);
    } else if (markerIndex == activeMarkers.length - 1) {
      indexes.push(markerIndex - 1);
    } else {
      indexes.push(markerIndex - 1);
      indexes.push(markerIndex);
    }

    console.log('[POLYLINES] Will update linestring indexes:', indexes);

    // Clear pending replacements - we're not tracking for undo anymore
    // Undo is saved BEFORE markerMoved is called
    state.pendingProvisionalReplacements.clear();

    for (let i = 0; i < indexes.length; i++) {
      const index1 = indexes[i];
      commit('removeLineStringAtIndex', { index: index1, routeKey });
      const startMarker = activeMarkers[index1];
      const endMarker = activeMarkers[index1 + 1];

      // Check if both markers exist before creating linestring
      if (startMarker && endMarker) {
        // Create a provisional line, let background fetch replace it
        const provisionalLine = await dispatch('createPolylineGeoJson', { startMarker, endMarker });
        commit('addLineStringAtIndex', { lineString: provisionalLine, index: index1, routeKey });
        console.log('[POLYLINES] Created provisional line (not tracked for undo):', provisionalLine.lineStringProperties.id);
      }
    }

    // Only update route polylines — markers already moved visually during drag
    await dispatch("updateRouteSources");
  },

  async addToPolyline(
    { state, dispatch, commit, getters }: PlanTrailActionContext,
    marker: MarkerFeature
  ) {
    console.log('[POLYLINES] addToPolyline called');

    const activeRoute = getters.activeRoute;
    if (!activeRoute) return;

    const n = activeRoute.markers.length;
    if (n < 2) return;

    const prev = activeRoute.markers[n - 2];

    // Clear and track the provisional line for this new segment
    state.pendingProvisionalReplacements.clear();
    console.log('[POLYLINES] Cleared pending provisional replacements');

    // 1) provisional
    const provisional = await dispatch("createPolylineGeoJson", {
      startMarker: prev,
      endMarker: marker
    });

    commit("addLineStringAtIndex", {
      lineString: provisional,
      index: activeRoute.lineStrings.length
    });

    // Track this provisional line so undo is saved when it's replaced
    state.pendingProvisionalReplacements.add(provisional.lineStringProperties.id);
    console.log('[POLYLINES] Added provisional line to tracking for addToPolyline:', provisional.lineStringProperties.id);
    console.log('[POLYLINES] Total pending:', state.pendingProvisionalReplacements.size);

    // Fallback: If GraphHopper doesn't respond within 5 seconds, save undo anyway
    setTimeout(() => {
      if (state.pendingProvisionalReplacements.size > 0) {
        console.log('[POLYLINES] Timeout reached for addToPolyline, saving undo state');
        state.pendingProvisionalReplacements.clear();
        commit('updateUndo');
      }
    }, 5000);

    // Update both routes and markers — new linestring + marker icons may change (first/last)
    await dispatch("updateRouteAndMarkers");
  },

  // Legacy function - removed. Use addAllRoutesToMap instead.

  // Legacy function - removed. Use addAllRoutesToMap instead.

  // Legacy function - removed. Use addAllRoutesToMap instead.

  // Legacy function - removed. Use createRouteLineLayer instead.

  // Legacy function - removed. Arrow layers are now handled in consolidated route rendering.

  // Consolidate all route line strings into a single FeatureCollection
  async addConsolidatedRoutesToMap({ state, dispatch }: PlanTrailActionContext) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();

    if (!state.routes || Object.keys(state.routes).length === 0) {
      return;
    }

    // Create separate FeatureCollections for active and inactive line strings
    const activeFeatures: any[] = [];
    const inactiveFeatures: any[] = [];
    const activeRouteKey = state.activeRouteKey;

    // Collect all line string features from all routes
    for (const [routeKey, route] of Object.entries(state.routes)) {
      const isActive = routeKey === activeRouteKey;

      route.lineStrings.forEach((lineString, lineIndex) => {
        try {
          // Convert plain object to LineStringFeature instance
          const lineStringInstance = plainToClass(LineStringFeature, lineString);
          const feature = lineStringInstance.toGeoJson();
          const baseProps = {
            ...feature.properties,
            routeId: route.id,
            lineIndex: lineIndex,
            routeKey: routeKey,
            routeStyle: route.route_style || 'route',
            // Still waiting for GraphHopper: drawn faint and dotted (see utils/pendingSnap.ts).
            pendingSnap: !!(lineString as any)?.lineStringProperties?.pendingSnap
          };
          const target = isActive ? activeFeatures : inactiveFeatures;

          // A leg pinned to an off-route destination routes as far as it can
          // (solid) then bridges the gap with a straight line (dashed). The
          // bridge is the FIRST segment (offNetworkHead) and/or the LAST
          // (offNetworkTail); the routed middle stays solid. We keep ONE
          // linestring in state (markers ↔ linestrings invariant) but split
          // it into solid + dashed FEATURES here so only the bridge dashes.
          const lsProps = (lineString as any)?.lineStringProperties || {};
          const head = !!lsProps.offNetworkHead;
          const tail = !!lsProps.offNetworkTail;
          const full = !!lsProps.offNetworkFull;
          const coords = (feature.geometry as any).coordinates as number[][];
          const N = coords.length;

          const pushSeg = (segCoords: number[][], dashed: boolean) => {
            if (!segCoords || segCoords.length < 2) return;
            target.push({
              type: 'Feature',
              bbox: feature.bbox,
              geometry: { type: 'LineString', coordinates: segCoords },
              properties: { ...baseProps, offNetwork: dashed }
            } as any);
          };

          if (!head && !tail && !full) {
            pushSeg(coords, false);
          } else if (full || N < 3) {
            // Whole leg is the bridge (straight-line mode, or too few points)
            pushSeg(coords, true);
          } else {
            if (head) pushSeg(coords.slice(0, 2), true);
            pushSeg(coords.slice(head ? 1 : 0, tail ? N - 1 : N), false);
            if (tail) pushSeg(coords.slice(N - 2, N), true);
          }
        } catch (error) {
          console.error(`Error processing line string ${lineIndex} for route ${routeKey}:`, error);
        }
      });
    }

    // If nothing to display, clear existing sources if present and exit
    if (activeFeatures.length === 0 && inactiveFeatures.length === 0) {
      const inactiveSourceId = 'consolidated-inactive-routes-lines';
      const activeSourceId = 'consolidated-active-routes-lines';
      const emptyFC = { type: 'FeatureCollection', features: [] } as any;
      const inactiveExisting = map.getSource(inactiveSourceId) as mapboxgl.GeoJSONSource | undefined;
      const activeExisting = map.getSource(activeSourceId) as mapboxgl.GeoJSONSource | undefined;
      if (inactiveExisting) { try { inactiveExisting.setData(emptyFC); } catch (_) {} }
      if (activeExisting) { try { activeExisting.setData(emptyFC); } catch (_) {} }
      return;
    }

    // IDs for split sources/layers
    const inactiveSourceId = 'consolidated-inactive-routes-lines';
    const inactiveLineLayerId = 'consolidated-inactive-routes-lines';
    const activeSourceId = 'consolidated-active-routes-lines';
    const activeLineLayerId = 'consolidated-active-routes-lines';
    const activeHoverLayerId = 'consolidated-active-routes-hover';
    const activeArrowLayerId = 'consolidated-active-routes-arrows';

    // Ensure sources exist and update via setData to avoid flashing
    const inactiveFC = { type: 'FeatureCollection', features: inactiveFeatures } as any;
    const activeFC = { type: 'FeatureCollection', features: activeFeatures } as any;

    const inactiveExisting = map.getSource(inactiveSourceId) as mapboxgl.GeoJSONSource | undefined;
    const activeExisting = map.getSource(activeSourceId) as mapboxgl.GeoJSONSource | undefined;

    if (inactiveExisting) {
      try { inactiveExisting.setData(inactiveFC); } catch (e) { console.warn('setData inactive failed; re-adding source'); layerRegistry.addSource(inactiveSourceId, { type: 'geojson', data: inactiveFC }, true); }
    } else {
      layerRegistry.addSource(inactiveSourceId, { type: 'geojson', data: inactiveFC }, true);
    }

    if (activeExisting) {
      try { activeExisting.setData(activeFC); } catch (e) { console.warn('setData active failed; re-adding source'); layerRegistry.addSource(activeSourceId, { type: 'geojson', data: activeFC }, true); }
    } else {
      layerRegistry.addSource(activeSourceId, { type: 'geojson', data: activeFC }, true);
    }

    // Create color expression based on route style
    const colorExpression: any = [
      'case',
      ['==', ['get', 'routeStyle'], 'route'], ROUTE_STYLES.route.color,
      ['==', ['get', 'routeStyle'], 'alt'], ROUTE_STYLES.alt.color,
      ['==', ['get', 'routeStyle'], 'spur'], ROUTE_STYLES.spur.color,
      ['==', ['get', 'routeStyle'], 'transport'], ROUTE_STYLES.transport.color,
      ROUTE_STYLES.route.color // default
    ];

    // Create border color expression based on route style
    const borderColorExpression: any = [
      'case',
      ['==', ['get', 'routeStyle'], 'route'], ROUTE_STYLES.route.borderColor,
      ['==', ['get', 'routeStyle'], 'alt'], ROUTE_STYLES.alt.borderColor,
      ['==', ['get', 'routeStyle'], 'spur'], ROUTE_STYLES.spur.borderColor,
      ['==', ['get', 'routeStyle'], 'transport'], ROUTE_STYLES.transport.borderColor,
      ROUTE_STYLES.route.borderColor // default
    ];

    // Create dash array expression for transport style + off-network tails
    const dashArrayExpression: any = [
      'case',
      ['==', ['get', 'routeStyle'], 'transport'],
      ['literal', ROUTE_STYLES.transport.dasharray || [1, 0]],
      ['==', ['get', 'pendingSnap'], true],
      ['literal', [0.5, 2]], // dotted: the real route is on its way
      ['==', ['get', 'offNetwork'], true],
      ['literal', [2, 2]], // dashed: "no known route" to an off-network item
      ['literal', [1, 0]] // solid line for everything else
    ];

    // Off-network tails have no border — like transport — so the dashes read
    // cleanly instead of being filled in by the casing beneath them.
    const inactiveBorderOpacityExpression: any = [
      'case',
      ['==', ['get', 'routeStyle'], 'transport'], 0,
      ['==', ['get', 'pendingSnap'], true], 0,
      ['==', ['get', 'offNetwork'], true], 0,
      0.4
    ];
    const activeBorderOpacityExpression: any = [
      'case',
      ['==', ['get', 'routeStyle'], 'transport'], 0,
      ['==', ['get', 'pendingSnap'], true], 0,
      ['==', ['get', 'offNetwork'], true], 0,
      1.0
    ];
    // The line itself fades while its route is on its way.
    const inactiveLineOpacityExpression: any = ['case', ['==', ['get', 'pendingSnap'], true], 0.25, 0.4];
    const activeLineOpacityExpression: any = ['case', ['==', ['get', 'pendingSnap'], true], 0.5, 1.0];

    // Add inactive border layer (thicker, low opacity) - exclude transport style
    const inactiveBorderLayerId = 'consolidated-inactive-routes-borders';
    if (!map.getLayer(inactiveBorderLayerId)) {
      const inactiveBorderLayer: mapboxgl.LineLayer = {
        id: inactiveBorderLayerId,
        type: 'line',
        source: inactiveSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': borderColorExpression,
          'line-width': 7, // Wider for border
          'line-opacity': inactiveBorderOpacityExpression,
          'line-dasharray': dashArrayExpression
        }
      };
      layerRegistry.addLayer(inactiveBorderLayer, MapLayerCategory.Annotations, { z: 7 });
    }

    // Add inactive line layer (thin, low opacity)
    if (!map.getLayer(inactiveLineLayerId)) {
      const inactiveLineLayer: mapboxgl.LineLayer = {
        id: inactiveLineLayerId,
        type: 'line',
        source: inactiveSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colorExpression,
          'line-width': 3,
          'line-opacity': inactiveLineOpacityExpression,
          'line-dasharray': dashArrayExpression
        }
      };
      layerRegistry.addLayer(inactiveLineLayer, MapLayerCategory.Annotations, { z: 8 });
    }

    // Add active border layer (thicker, high opacity) - exclude transport style
    const activeBorderLayerId = 'consolidated-active-routes-borders';
    if (!map.getLayer(activeBorderLayerId)) {
      const activeBorderLayer: mapboxgl.LineLayer = {
        id: activeBorderLayerId,
        type: 'line',
        source: activeSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': borderColorExpression,
          'line-width': 9, // Wider for border
          'line-opacity': activeBorderOpacityExpression,
          'line-dasharray': dashArrayExpression
        }
      };
      layerRegistry.addLayer(activeBorderLayer, MapLayerCategory.Annotations, { z: 8 });
    }

    // Add active line layer (thicker, high opacity)
    if (!map.getLayer(activeLineLayerId)) {
      const activeLineLayer: mapboxgl.LineLayer = {
        id: activeLineLayerId,
        type: 'line',
        source: activeSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colorExpression,
          'line-width': 5,
          'line-opacity': activeLineOpacityExpression,
          'line-dasharray': dashArrayExpression
        }
      };
      layerRegistry.addLayer(activeLineLayer, MapLayerCategory.Annotations, { z: 9 });
    }

    // Add hover detection layer
    const currentZoom = map.getZoom();
    const hoverWidth = HoverOptimizationConfig.getHoverLayerWidth(currentZoom);

    if (!map.getLayer(activeHoverLayerId)) {
      const hoverLayer: mapboxgl.LineLayer = {
        id: activeHoverLayerId,
        type: 'line',
        source: activeSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#000000',
          'line-width': hoverWidth,
          'line-opacity': 0.01
        }
      };
      layerRegistry.addLayer(hoverLayer, MapLayerCategory.Annotations, { z: 11 });
    }

    // Add arrow layer for active routes using sprite sheet image
    if (!map.getLayer(activeArrowLayerId)) {
      const arrowLayer = {
        id: activeArrowLayerId,
        type: 'symbol',
        source: activeSourceId,
        layout: {
          'symbol-placement': 'line',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': 'hike-arrow-icon',
          'icon-size': 0.2,
          'visibility': 'visible'
        }
      } as any;
      layerRegistry.addLayer(arrowLayer, MapLayerCategory.Annotations, { z: 11 });
    }

    // Non-active route lines are click-to-edit targets (auto-activate) —
    // show a pointer so they read as interactive. Re-asserted per-move
    // because the global hover handler resets the cursor on every mousemove.
    if (!inactiveLineHoverAttached && map.getLayer(inactiveLineLayerId)) {
      const lineHoverCanvas = map.getCanvas();
      const inactiveLineHover = () => { lineHoverCanvas.style.cursor = 'pointer'; };
      const inactiveLineLeave = () => { lineHoverCanvas.style.cursor = 'crosshair'; };
      map.on('mousemove', inactiveLineLayerId, inactiveLineHover);
      map.on('mouseleave', inactiveLineLayerId, inactiveLineLeave);
      inactiveLineHoverAttached = true;
    }

    // Apply custom route style settings (color, thickness, opacity) from localStorage.
    // NOTE: non-active routes render at FULL opacity but in MUTED colors — an
    // opacity fade made them hard to follow, while a dusty version of each
    // route color keeps the full multi-day line readable and still makes the
    // active day pop (full color, wider line, arrows and markers).
    const customExprs = buildRouteStyleExpressions(getRouteStyleSettings());
    // Muted colours only make sense relative to an ACTIVE route. With
    // nothing selected (deactivated state) the whole trail renders in full
    // colour and is editable via auto-activate-on-touch.
    const hasActiveRoute = !!(state.activeRouteKey && state.routes[state.activeRouteKey]);
    const inactiveColorExpr = hasActiveRoute ? customExprs.mutedColorExpr : customExprs.colorExpr;
    const inactiveBorderExpr = hasActiveRoute ? customExprs.mutedBorderColorExpr : customExprs.borderColorExpr;
    if (map.getLayer(inactiveBorderLayerId)) {
      map.setPaintProperty(inactiveBorderLayerId, 'line-color', inactiveBorderExpr as any);
      map.setPaintProperty(inactiveBorderLayerId, 'line-width', customExprs.inactiveBorderWidthExpr as any);
      map.setPaintProperty(inactiveBorderLayerId, 'line-opacity', customExprs.activeBorderOpacityExpr as any);
    }
    if (map.getLayer(inactiveLineLayerId)) {
      map.setPaintProperty(inactiveLineLayerId, 'line-color', inactiveColorExpr as any);
      map.setPaintProperty(inactiveLineLayerId, 'line-width', customExprs.inactiveLineWidthExpr as any);
      map.setPaintProperty(inactiveLineLayerId, 'line-opacity', customExprs.activeLineOpacityExpr as any);
    }
    if (map.getLayer(activeBorderLayerId)) {
      map.setPaintProperty(activeBorderLayerId, 'line-color', customExprs.borderColorExpr as any);
      map.setPaintProperty(activeBorderLayerId, 'line-width', customExprs.activeBorderWidthExpr as any);
      map.setPaintProperty(activeBorderLayerId, 'line-opacity', customExprs.activeBorderOpacityExpr as any);
    }
    if (map.getLayer(activeLineLayerId)) {
      map.setPaintProperty(activeLineLayerId, 'line-color', customExprs.colorExpr as any);
      map.setPaintProperty(activeLineLayerId, 'line-width', customExprs.activeLineWidthExpr as any);
      map.setPaintProperty(activeLineLayerId, 'line-opacity', customExprs.activeLineOpacityExpr as any);
    }

    // Update hover width on zoom changes
    if (!state._consolidatedHoverZoomHandler) {
      state._consolidatedHoverZoomHandler = () => {
        const newZoom = map.getZoom();
        const newWidth = HoverOptimizationConfig.getHoverLayerWidth(newZoom);

        if (map.getLayer(activeHoverLayerId)) {
          map.setPaintProperty(activeHoverLayerId, 'line-width', newWidth);
        }
      };

      map.on('zoomend', state._consolidatedHoverZoomHandler);
    }
  },

  // Route-specific line string actions
  async addRouteLineStringToMap({ state, dispatch }: PlanTrailActionContext, data: {
    lineStringFeature: LineStringFeature,
    routeId: string,
    lineIndex: number,
    isActive: boolean
  }) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();
    const { routeId, lineIndex, isActive } = data;

    // Remove existing layer if it exists
    await dispatch("removeRouteLineStringFromMap", { routeId, lineIndex });

    const stringId = `route-${routeId}-line-${lineIndex}`;
    const hoverLayerId = `route-${routeId}-line-${lineIndex}-hover`;
    const arrowLayer = `route-${routeId}-arrow-${lineIndex}`;

    const lineLayer = await dispatch("createRouteLineLayer", {
      stringId,
      isActive
    });

    // Add source with replace flag to ensure clean state
    const lineString = plainToClass(LineStringFeature, data.lineStringFeature);
    layerRegistry.addSource(stringId, lineString.toGeoJsonSource(), true);

    // Add the visible line layer
    layerRegistry.addLayer(lineLayer, MapLayerCategory.Annotations, { z: isActive ? 10 : 8 });

    // Add invisible hover detection layer for active routes
    if (isActive) {
      // Get current zoom level for dynamic hover width
      const currentZoom = map.getZoom();
      const hoverWidth = HoverOptimizationConfig.getHoverLayerWidth(currentZoom);

      // Create a wider invisible line for hover detection
      const hoverLayer = {
        id: hoverLayerId,
        type: "line",
        source: stringId,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "transparent",
          "line-width": hoverWidth, // Dynamic width based on zoom
          "line-opacity": 0
        }
      };
      layerRegistry.addLayer(hoverLayer, MapLayerCategory.Annotations, { z: 11 });

      // Update hover width on zoom changes
      if (!state._hoverZoomHandler) {
        state._hoverZoomHandler = () => {
          const newZoom = map.getZoom();
          const newWidth = HoverOptimizationConfig.getHoverLayerWidth(newZoom);

          // Update all hover layers
          if (state.routes) {
            Object.keys(state.routes).forEach(routeId => {
              const route = state.routes[routeId];
              route.lineStrings.forEach((_, idx) => {
                const layerId = `route-${routeId}-line-${idx}-hover`;
                if (map.getLayer(layerId)) {
                  map.setPaintProperty(layerId, 'line-width', newWidth);
                }
              });
            });
          }
        };

        map.on('zoomend', state._hoverZoomHandler);
      }

      await dispatch('addPolylineArrowLayerToMap', {
        targetLayerId: stringId,
        arrowLayer
      });
    }
  },

  async removeRouteLineStringFromMap({ }: PlanTrailActionContext, data: { routeId: string, lineIndex: number }) {
    const { routeId, lineIndex } = data;
    const stringId = `route-${routeId}-line-${lineIndex}`;
    const hoverLayerId = `route-${routeId}-line-${lineIndex}-hover`;
    const arrowLayer = `route-${routeId}-arrow-${lineIndex}`;

    // Remove hover layer first (if it exists)
    safeRemoveLayer(hoverLayerId);

    // Remove arrow layer (if it exists)
    safeRemoveLayer(arrowLayer);

    // Remove main line layer and source
    safeRemoveLayer(stringId);
  },

  createRouteLineLayer({ }: PlanTrailActionContext, data: { stringId: string, isActive: boolean }): mapboxgl.LineLayer {
    const { stringId, isActive } = data;
    return {
      id: stringId,
      type: "line",
      source: stringId,
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": isActive ? "#fd5372" : "#fd5372",
        "line-width": isActive ? 5 : 3,
        "line-opacity": isActive ? 1.0 : 0.4,
      },
    };
  }
};
